import React, {Component} from 'react';
import * as Sentry from '@sentry/browser';
import PropTypes from 'prop-types';
import {withRouter} from 'react-router';
import {Icon} from 'semantic-ui-react';
import styled from 'styled-components';
import {isNil} from 'ramda';
import {LinkButton} from '../components/Button/ButtonMix';
import Sidebar from '../components/Sidebar/Sidebar';
import {
  clear,
  getUserProperty,
  isOwesSubscriptionMoney,
  isOwesTeamMoney,
  userHasRole
} from '../util/Session';
import {retrieveMySchool, retrieveMyTeam} from '../api';

const SideMenu = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh);
  overflow: hidden;
  padding: 5px;
`;

const MenuButton = styled(LinkButton)`
  &.ui.button{
    background-color: transparent;
    color: #666666;
    font-family: Roboto;
    font-size: 14px;
    letter-spacing: 0.87px;
    line-height: 16px;
    text-align: left;
    padding: 5px 15px;
    margin: 0;
    font-weight: normal;
    height: 32px;
    &.active {
      background-color: #0A8DE2;
      color: #fff;
      font-weight: bold;
    }
  }
`;

const MenuButtonLogout = styled(MenuButton)`
  &.ui.button{
    color: #F74747;
    font-weight: bold;
  }
`;

const MenuHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: #F2F2F2;
  margin: -5px;
  padding: 20px;
  border-bottom: 1px solid #D8D8D8;
  & > h2 {
    color: #2E2E33;
    font-family: Roboto;
    font-size: 18px;
    font-weight: bold;
    letter-spacing: 1.12px;
    line-height: 21px;
    & > span {
      margin-top: 5px;
      color: #666666;
      font-size: 14px;
      letter-spacing: 0.87px;
      line-height: 16px;
      font-weight: normal;
      display: block;
    }
  }
`;

const MenuSubHeader = styled.div`
  margin: 5px -5px 0;
  padding: 5px 20px;  
  border-bottom: 1px solid #D8D8D8;
  & > h3 {
    font-family: Roboto;
    font-size: 14px;
    font-weight: bold;
    letter-spacing: 1.12px;
  }
`;

const StyledIcon = styled(Icon)`
  color: #707070;
  opacity: .5 !important;
  position: absolute;
  top: 20px;
  right: 20px;
`;

const SchoolDollars = styled.div`
  margin-top: 20px;
  padding-top: 5px;
  color: #00cc66;
  & > h4 {
    color: inherit;
    & > span {
      color: #fff;
      background-color: #00cc66;
      display: inline;
      padding: 3px 5px;
      border-radius: 5px;
    }
  }
  & > p {
    margin-top: 5px;
    font-weight: 100;
  }
`;

const FamCommunity = styled.div`
  margin-top: 20px;
  padding-top: 5px;
  color: #0A8DE2;
  & > h4 {
    color: inherit;
    & > span {
      color: #fff;
      background-color: #05CFEC;
      display: inline;
      padding: 3px 5px;
      border-radius: 5px;
    }
  }
  & > p {
    margin-top: 5px;
    font-weight: 100;
  }
`;

const MenuWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding-bottom: 20px;
  margin-top: 20px;
`;

const Space = styled.div`
  flex: 1;
`;

class LoggedInApp extends Component {
  constructor(props) {
    super(props);
    Sentry.configureScope((scope) => {
      scope.setUser({email: getUserProperty('email')});
    });

    this.state = {
      isShowSidebar: false,
      team: null,
      school: null
    };
  }

  componentDidMount() {
    return retrieveMyTeam()
      .then(response => {
        this.setState({team: response.data});
        return retrieveMySchool();
      })
      .then(response => {
        this.setState({school: response.data});
      });
  }

  handleButtonClick = () => this.setState({isShowSidebar: !this.state.isShowSidebar});

  handleSidebarHide = () => this.setState({isShowSidebar: false});

  onLogout = () => {
    const {router} = this.props;
    clear();
    this.handleSidebarHide();
    router.push('/login');
  };

  onDashboard = () => {
    const {router} = this.props;
    this.handleSidebarHide();
    router.push('/dashboard');
  };

  onProgress = () => {
    const {router} = this.props;
    this.handleSidebarHide();
    router.push('/progress');
  };

  onEditProfile = () => {
    const {router} = this.props;
    this.handleSidebarHide();
    router.push('/profile');
  };

  onMyTeam = () => {
    const {router} = this.props;
    this.handleSidebarHide();
    router.push('/team');
  };

  onViewStudents = () => {
    const {router} = this.props;
    this.handleSidebarHide();
    router.push('/schools/students');
  };

  onViewStaffPoints = () => {
    const {router} = this.props;
    this.handleSidebarHide();
    router.push('/schools/staff');
  };

  onViewUsers = () => {
    const {router} = this.props;
    this.handleSidebarHide();
    router.push('/schools/users');
  };

  onViewSemesters = () => {
    const {router} = this.props;
    this.handleSidebarHide();
    router.push('/schools/semesters');
  };

  isActive = (location) => {
    const {router} = this.props;
    const isActive = router.getCurrentLocation().pathname === location;
    return isActive ? 'active' : '';
  }

  render() {
    const {children} = this.props;
    const {team, school} = this.state;
    const fullName = `${getUserProperty('first_name')} ${getUserProperty('last_name')}`;
    const schoolName = isNil(school) ? null : school.name;
    const teamName = isNil(team) ? null : `Team ${team.name}`;
    const isTeacher = userHasRole('staff');
    const isAdmin = userHasRole('admin');
    const numTeamUsers = !isNil(team) ? team.num_team_users : null;
    const numSchoolTeamUsers = !isNil(school) ? school.num_team_users : null;
    const isOwesMoney = isOwesSubscriptionMoney() || isOwesTeamMoney();

    const subHeaderName = userHasRole('community_member') ? teamName : schoolName;

    const TeacherMenuItems = (
      <div>
        <MenuSubHeader>
          <h3>Staff use</h3>
        </MenuSubHeader>
        <MenuButton className={this.isActive('/schools/students')} onClick={this.onViewStudents}>Student
          points
        </MenuButton>
      </div>
    );

    const AdminMenuItems = (
      <div>
        <MenuSubHeader>
          <h3>Coordinator use</h3>
        </MenuSubHeader>
        <MenuButton className={this.isActive('/schools/staff')} onClick={this.onViewStaffPoints}>Staff
          points
        </MenuButton>
        <MenuButton className={this.isActive('/schools/users')} onClick={this.onViewUsers}>Staff & students</MenuButton>
        <MenuButton className={this.isActive('/schools/semesters')} onClick={this.onViewSemesters}>Schedule interval
          settings
        </MenuButton>
      </div>
    );

    const SidebarMenuItems = (
      <SideMenu>
        <MenuHeader>
          <h2>
            {fullName}
            <span>{subHeaderName}</span>
          </h2>
          <SchoolDollars>
            <h4>
              <span>{numSchoolTeamUsers}</span> influenced by our school
            </h4>
          </SchoolDollars>
          <FamCommunity>
            <h4>
              <span>{numTeamUsers}</span> influenced by me
            </h4>
          </FamCommunity>
          <StyledIcon name="window close outline" onClick={this.handleSidebarHide}/>
        </MenuHeader>

        {isAdmin && !isOwesMoney && AdminMenuItems}
        {isTeacher && !isOwesMoney && TeacherMenuItems}

        <MenuWrapper>
          <MenuButton className={this.isActive('/dashboard')} onClick={this.onDashboard}>Home</MenuButton>
          <MenuButton className={this.isActive('/progress')} onClick={this.onProgress}>Progress</MenuButton>
          <MenuButton className={this.isActive('/profile')} onClick={this.onEditProfile}>User profile</MenuButton>
          <MenuButton className={this.isActive('/teams')} onClick={this.onMyTeam}>My team ranking</MenuButton>
          <Space/>
          <MenuButtonLogout onClick={this.onLogout}>Logout</MenuButtonLogout>
        </MenuWrapper>
      </SideMenu>
    );

    return (
      <Sidebar isVisible={this.state.isShowSidebar} onHide={this.handleSidebarHide} menuItems={SidebarMenuItems}>
        {React.cloneElement(children, {onClickSidebar: this.handleButtonClick})}
      </Sidebar>
    );
  }
}

LoggedInApp.propTypes = {
  children: PropTypes.element.isRequired,
  router: PropTypes.object.isRequired
};

export default withRouter(LoggedInApp);



// WEBPACK FOOTER //
// ./src/pages/LoggedInApp.js